import TablePaginantion from "@/components/common/table/pagination";
import Filter from "@/components/ui/filters";
import NoData from "@/components/ui/no-data";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthorization } from "@/hooks/use-authorization";
import { usePagination } from "@/hooks/usePagination";
import {
  useGetProcesses,
  useUpdateProcess,
  useArchiveProcess,
  useGetProcess,
} from "@/queries/process/process.api";
import { useGetUsersAsInfinite } from "@/queries/users/users.api";
import type {
  CurrentFiltersType,
  FilterTypeList,
  MultiEntitySelections,
  SortOptionsTypes,
} from "@/types/common/filters";
import { type ProcessType } from "@/types/entities/process";
import { Permissions } from "@/utils/permissions";
import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { useState } from "react";
import {
  type SortDirectionType,
  filterTypesEnum,
  searchTypesEnum,
  sortDirectionEnum,
} from "../../types/common/filters";
import { EditProcessModal } from "./components/EditProcessModal";
import NewProcessModal from "./components/NewProcessModal";
import ProcessCard from "./components/ProcessCard";
import SortDropdown from "./components/SortDropdown";
import { PageTabs } from "@/components/ui/page-tabs";
import TagsList from "./components/TagsList";
import SearchInput from "@/components/common/search-input";
import { useDebounceValue } from "usehooks-ts";
import { Renderify as Render } from "renderify-js";
import { useGetTagsAsInfinite } from "@/queries/tags/tags.api";
import { NavLink } from "react-router";
import DuplicateProcessModal from "./components/DuplicateProcessModal";
import { STATUS_CONFIG, type ProcessStatus } from "./constants";
import ArchiveProcessModal from "./components/ArchiveProcessModal";
import ConfirmUnArchiveModal from "./components/ConfirmUnArchiveModal";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";

type ProcessTabType = "procesos" | "etiquetas";
const processTabs = [
  {
    label: "Procesos",
    key: "procesos",
  },
  {
    label: "Etiquetas",
    key: "etiquetas",
  },
];

const sortFields: SortOptionsTypes[] = [
  { label: "Nombre", value: "name" },
  { label: "Estado", value: "status" },
  { label: "Fecha de actualización", value: "updated_at" },
  { label: "Fecha de creación", value: "created_at" },
  { label: "Usuario", value: "created_by" },
];

const FilterFields: FilterTypeList[] = [
  {
    label: "Última Actualización",
    type: filterTypesEnum.dates,
    field: "updated_at",
  },
  {
    label: "Estado",
    type: filterTypesEnum.status,
    field: "status",
    options: [
      { label: "Activo", field: "PUBLISHED" },
      { label: "Archivado", field: "INACTIVE" },
      { label: "En Construcción", field: "ON_CONSTRUCTION" },
    ],
  },

  {
    label: "Creador del proceso",
    type: filterTypesEnum.textsearch,
    field: "created_by",
    searchField: "search",
    query: useGetUsersAsInfinite,
    searchType: searchTypesEnum.user,
  },
  {
    label: "Etiqueta",
    type: filterTypesEnum.textsearch,
    field: "tags",
    query: useGetTagsAsInfinite,
    searchField: "filter[search]",
    searchType: searchTypesEnum.tags,
  },
];

const per_page = 20;

const ProcesosPage = () => {
  const [selectedTab, setSelectedTab] = useState<ProcessTabType>("procesos");

  return (
    <>
      <PageTabs
        className="mx-2"
        activeKey={selectedTab}
        onTabChange={(tab) => setSelectedTab(tab as ProcessTabType)}
        tabs={processTabs}
      />
      {selectedTab === "procesos" ? <ProcessList /> : <TagsList />}
    </>
  );
};

export const ProcessList = () => {
  const { hasPermission } = useAuthorization();
  const { showToast } = useToast();
  const canCreate = hasPermission(Permissions.processes.create);

  const [modalState, setModalState] = useState<
    "idle" | "new" | "edit" | "duplicate-flow" | "archive" | "unarchive"
  >("idle");
  const [editProcess, setEditProcess] = useState<string | null>(null);

  const { data: editProcessData, isPending: isLoadingData } = useGetProcess(
    editProcess || undefined,
  );

  const [search, setSearch] = useQueryState(
    "search",
    parseAsString.withDefault(""),
  );

  const [deferredQuery] = useDebounceValue(search || "", 500);

  const [orderField, setOrderField] = useQueryState(
    "order_field",
    parseAsString.withDefault("created_at"),
  );

  const [orderDirection, setOrderDirection] = useQueryState(
    "order_direction",
    parseAsString.withDefault(sortDirectionEnum.desc),
  );
  const [updatedAt, setUpdatedAt] = useQueryState("updated_at", parseAsString);
  const [status, setStatus] = useQueryState("status", parseAsString);
  const [createdBy, setCreatedBy] = useQueryState("created_by", parseAsString);
  const [tags, setTags] = useQueryState("tags", parseAsString);

  const [filters, _setFilters] = useState<CurrentFiltersType[]>(() => {
    const initialFilters: CurrentFiltersType[] = [];
    if (updatedAt) {
      initialFilters.push({ updated_at: updatedAt });
    }
    if (status) {
      initialFilters.push({ status });
    }
    if (createdBy) {
      initialFilters.push({ created_by: createdBy });
    }
    if (tags) {
      initialFilters.push({ tags });
    }
    return initialFilters;
  });

  const setFilters = (newFilters: CurrentFiltersType[]) => {
    console.log("newFilters", newFilters);
    _setFilters(newFilters);
    setPage(1);

    // Resetear filtros al dar click en limpiar filtros
    setUpdatedAt(null);
    setStatus(null);
    setCreatedBy(null);
    setTags(null);

    newFilters.forEach((f) => {
      if (f.updated_at !== undefined) {
        setUpdatedAt(f.updated_at);
      }
      if (f.status !== undefined) {
        setStatus(f.status);
      }
      if (f.created_by !== undefined) {
        setCreatedBy(f.created_by);
      }
      if (f.tags !== undefined) {
        setTags(f.tags);
      }
    });
  };

  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});

  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));

  const { mutateAsync: mutateProcess, isPending: isUpdateLoading } =
    useUpdateProcess();
  const { mutateAsync: archiveProcessMutate } = useArchiveProcess();

  const {
    data: processesData,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useGetProcesses({
    filter: {
      ...(status ? { status } : {}),
      ...(updatedAt ? { updated_at: updatedAt } : {}),
      ...(createdBy ? { created_by: createdBy } : {}),
      ...(tags ? { tags } : {}),
      ...(deferredQuery
        ? {
            search: deferredQuery,
          }
        : {}),
    },
    relationships: ["createdBy", "files", "tags"],
    // filter:
    //   filters?.length > 0
    //     ? {
    //         ...mergeFilters(filters),
    //         ...(deferredQuery
    //           ? {
    //               search: deferredQuery,
    //             }
    //           : {}),
    //       }
    //     : {
    //         ...(deferredQuery
    //           ? {
    //               search: deferredQuery,
    //             }
    //           : {}),
    //       },
    order_by: orderField
      ? `${orderDirection === "desc" ? "!" : ""}${orderField}`
      : undefined,
    page,
    per_page,
  });

  const {
    currentPage,
    totalItems,
    hasNextPage,
    hasPreviousPage,
    onNext,
    onPrevious,
    onPageChange,
    pageSize,
    totalPages,
  } = usePagination({
    meta: processesData?.meta,
    links: processesData?.links,
    onPageUpdate: (page) => {
      setPage(page);
    },
  });

  const handleOpenModal = () => setModalState("new");
  const handleCloseModal = () => setModalState("idle");
  const handleSuccess = () => {
    refetch();
    showToast({
      title: "Proceso Creado",
      description: "El proceso ha sido creado correctamente.",
      options: {
        type: "success",
        duration: 5000,
      },
    });
  };

  const handleapplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setPage(1);
    setOrderField(field || null);
    setOrderDirection(direction);
  };

  const handleApproved = async (
    id: string,
    currentStatus: ProcessStatus,
    reason?: string,
  ) => {
    const nextStatus = STATUS_CONFIG[currentStatus]?.next ?? "ACTIVE";
    const translates = {
      ON_CONSTRUCTION: "activado",
      PUBLISHED: "archivado",
      INACTIVE: "desarchivado",
    };

    if (
      nextStatus === "INACTIVE" &&
      currentStatus !== "ON_CONSTRUCTION" &&
      modalState !== "archive"
    ) {
      setModalState("archive");
      return;
    }

    if (
      nextStatus === "PUBLISHED" &&
      currentStatus !== "ON_CONSTRUCTION" &&
      modalState !== "unarchive"
    ) {
      setModalState("unarchive");
      return;
    }

    try {
      // Para desarchivar (INACTIVE -> PUBLISHED), usar el endpoint de archivar con reason
      if (currentStatus === "INACTIVE") {
        await archiveProcessMutate({
          id,
          payload: { status: nextStatus, reason },
        });
      } else {
        await mutateProcess({
          id,
          payload: { status: nextStatus },
        });
      }
      showToast({
        title: `Proceso ${translates[currentStatus]}`,
        description: `El proceso ha sido ${translates[currentStatus]} correctamente.`,
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error: unknown) {
      // Check for specific error code from backend
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data &&
        error.response.data.error === "PROCESS_NO_APPROVED_FLOWS"
      ) {
        const errorData = error.response.data as unknown as { message: string };
        showToast({
          title: `Error al ${translates[currentStatus]}`,
          description: errorData.message,
          options: {
            type: "error",
            duration: 5000,
          },
        });
      } else {
        showToast({
          title: `Error al ${translates[currentStatus]}`,
          description: `No se pudo ${translates[currentStatus]} el proceso. Intenta nuevamente.`,
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
    }
    refreshData();
  };

  const refreshData = () => {
    if (currentPage === 1) {
      refetch();
    } else {
      setPage(1);
    }
  };

  const processes = processesData?.data || [];

  return (
    <>
      {modalState === "new" && (
        <NewProcessModal
          open={modalState === "new"}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
        />
      )}
      {modalState === "edit" && (
        <EditProcessModal
          isOpen={modalState === "edit"}
          modalData={editProcessData?.data}
          processId={editProcess || ""}
          isLoadingData={editProcessData === undefined || isLoadingData}
          onClose={() => {
            handleCloseModal();
            setEditProcess(null);
          }}
          onSuccess={() => {
            refreshData();
            showToast({
              title: "Proceso actualizado",
              description: "El proceso ha sido actualizado correctamente.",
              options: {
                type: "success",
                duration: 5000,
              },
            });
          }}
        />
      )}
      {modalState === "duplicate-flow" && (
        <DuplicateProcessModal
          processId={editProcess || ""}
          modalData={editProcessData?.data}
          isOpen={modalState === "duplicate-flow"}
          onClose={() => {
            setModalState("idle");
          }}
        />
      )}

      {modalState === "archive" && (
        <ArchiveProcessModal
          isOpen={modalState === "archive"}
          onClose={() => setModalState("idle")}
          processId={editProcess || ""}
        />
      )}

      {modalState === "unarchive" && (
        <ConfirmUnArchiveModal
          open={modalState === "unarchive"}
          onClose={() => setModalState("idle")}
          onDelete={async (id, _status, reason) => {
            await handleApproved(id, "INACTIVE", reason);
            setModalState("idle");
          }}
          id={editProcess || ""}
        />
      )}

      <div className="w-full h-full flex flex-col space-y-2 p-1">
        <Render
          condition={
            !isLoading &&
            !isFetching &&
            processes.length === 0 &&
            filters.length === 0 &&
            !deferredQuery
          }
          elseShow={
            <>
              <ProcessListHeader />
              <div className="flex justify-between gap-2">
                <div className="flex space-x-3">
                  <SortDropdown
                    currentOrderField={orderField}
                    currentOrderDirection={orderDirection as SortDirectionType}
                    sortOptions={sortFields}
                    onClick={handleapplySort}
                  />
                  <Filter
                    filters={FilterFields}
                    currentFilters={filters}
                    onCurrentFiltersChange={setFilters}
                    selecteds={selectedEntities}
                    onSetSelecteds={setSelectedEntities}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div className="hidden md:inline">
                    <SearchInput
                      variant="sm"
                      placeholder="Buscar procesos..."
                      className="w-[140px] laptop:w-[240px] text-sm"
                      value={search || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setPage(1);
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      variant="default"
                      size="sm"
                      className="items-center gap-2 cursor-pointer py-1.5 px-2"
                      onClick={handleOpenModal}
                    >
                      Nuevo proceso
                      <Icon type="far" name="plus" className="text-current" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="w-full flex flex-col mt-2 gap-2 min-h-[602px]">
                <Render
                  condition={processes.length > 0}
                  elseShow={
                    <Render
                      condition={!(isLoading || isUpdateLoading)}
                      elseShow={
                        <div
                          className="w-full flex flex-col mt-2 gap-2"
                          data-testid="page-loader"
                        >
                          {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                            <Skeleton
                              key={item}
                              className="w-full gap-4 h-[68px] rounded-xl px-4 py-3 "
                            />
                          ))}
                        </div>
                      }
                    >
                      <Render
                        condition={!isError}
                        elseShow={
                          <div className="w-full h-full flex items-center justify-center text-danger text-sm">
                            Error al cargar los procesos.
                          </div>
                        }
                      >
                        <div className="w-full flex justify-center items-center min-h-[500px]">
                          <h2 className="text-heading-quiet font-semibold text-2xl">
                            Sin procesos que mostrar
                          </h2>
                        </div>
                      </Render>
                    </Render>
                  }
                >
                  <>
                    {processes.map((process) => (
                      <NavLink key={process.id} to={`/procesos/${process.id}`}>
                        <ProcessCard
                          key={process.id}
                          process={process}
                          onDuplicate={(process: ProcessType) => {
                            setEditProcess(process?.id);
                            setModalState("duplicate-flow");
                          }}
                          onApproved={() => {
                            setEditProcess(process?.id);
                            handleApproved(process.id, process.status);
                          }}
                          onSelectEdit={(process: ProcessType) => {
                            setEditProcess(process?.id);
                            setModalState("edit");
                          }}
                        />
                      </NavLink>
                    ))}

                    {processesData?.meta !== undefined &&
                      processesData?.links !== undefined && (
                        <div className="p-3">
                          <TablePaginantion
                            currentPage={currentPage}
                            totalItems={totalItems}
                            hasNext={hasNextPage}
                            hasPrevious={hasPreviousPage}
                            onNext={onNext}
                            onPageChange={onPageChange}
                            onPrevious={onPrevious}
                            pageSize={pageSize}
                            totalPages={totalPages}
                          />
                        </div>
                      )}
                  </>
                </Render>
              </div>
            </>
          }
        >
          <div className="flex flex-col justify-center items-center min-h-[calc(100vh-210px)]">
            <NoData
              title=" Aún no existen procesos creados"
              description="Crea un proceso para poder editar sus detalles"
            >
              {canCreate && (
                <Button
                  variant="default"
                  size="sm"
                  className="items-center gap-2 rounded-lg cursor-pointer leading-2xl"
                  onClick={handleOpenModal}
                >
                  Nuevo proceso
                  <Icon type="far" name="plus" className="text-current" />
                </Button>
              )}
            </NoData>
          </div>
        </Render>
      </div>
    </>
  );
};

const ProcessListHeader = () => {
  return (
    <p className="font-geist text-body font-normal text-base mt-8 mb-6">
      Crea los procesos que realizaras para los trámites de tu institución
    </p>
  );
};

export default ProcesosPage;
